import { TrendingUp, Trophy, Building2, Target, Users, Info } from 'lucide-preact'
import { ToggleRow } from '@/components/common/ToggleRow'
import type { HubSpotMappingConfig } from '@/types/hubspot'

interface DealDataSyncStepProps {
    config: HubSpotMappingConfig
    onChange: (config: HubSpotMappingConfig) => void
}

export function DealDataSyncStep({ config, onChange }: DealDataSyncStepProps) {
    const dealConfig = config.DealConfig

    const updateDealConfig = (updates: Partial<typeof dealConfig>) => {
        onChange({ ...config, DealConfig: { ...dealConfig, ...updates } })
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Deal Data Sync</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Configure revenue attribution data sync from your CRM to OrbitalX.
                </p>
            </div>

            {/* Enable toggle */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">Enable Deal Data Sync</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Control whether revenue attribution data is shown for the workspace.</p>
                    </div>
                    <ToggleRow checked={dealConfig.Enabled} onChange={() => updateDealConfig({ Enabled: !dealConfig.Enabled })} ariaLabel="Enable Deal Data Sync" />
                </div>
            </div>

            {dealConfig.Enabled && (
                <>
                    {/* Feature cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-primary-500" />
                                <span className="text-sm font-medium text-slate-900 dark:text-white">Pipeline Generated</span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Track total potential revenue influenced by campaigns.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
                            <div className="flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-primary-500" />
                                <span className="text-sm font-medium text-slate-900 dark:text-white">Closed Won</span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Attribute actual realized revenue to specific touchpoints.</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
                            <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-primary-500" />
                                <span className="text-sm font-medium text-slate-900 dark:text-white">Company Visibility</span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">See deal progression at the company level in OrbitalX.</p>
                        </div>
                    </div>

                    {/* Attribution summary */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
                        <div>
                            <h4 className="font-medium text-sm text-slate-900 dark:text-white">Attribution Summary</h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">How deals are attributed to campaigns based on your CRM spec.</p>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                <Target className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-slate-900 dark:text-white">Named Accounts Logic</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                        If a deal is associated with a company in your Named Accounts list, attribution is weighted towards account-based marketing efforts.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-slate-900 dark:text-white">Non-Audience Logic</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                        Deals outside of target audiences are tracked but categorized separately to maintain focus on primary ICP metrics.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                            <Info className="w-4 h-4 mt-0.5 shrink-0" />
                            <p><strong>Note on Closed-Lost:</strong> Deals marked as closed-lost will still count toward your "Pipeline Generated" metrics for historical context, but will not be included in "Closed Won" revenue calculations.</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
